import { useState } from 'react';
import { MessageDirection, ChannelType } from 'src/api/generated';
import { format } from 'date-fns';
import { MdAttachment } from 'react-icons/md';
import { ChannelIcon } from 'src/modules/shared/components/atoms/icons/channel-icon';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { MessageStatusIndicator } from 'src/modules/shared/components/atoms/indicators/message-status-indicator';
import { classes } from 'src/modules/shared/utils/styles.util';
import { TimelineItemActions } from './timeline-item-actions';
import { TimelineItemDetails } from './timeline-item-details';
import { TimelineItemShell } from './timeline-item-shell';
import { TimelineContextBadges } from './timeline-context-badges';
import type { TimelineMessageItemProps } from '../../../types/timeline/compact-timeline.types';
import {
  getContextBorderTintClass,
  getContextOpacityClass,
} from '../../../utils/timeline/item-classes.util';

/** Compact subject and preview; the full email remains available on demand. */
export function TimelineEmailItem({
  item,
  isNew = false,
  contact,
  context,
}: TimelineMessageItemProps) {
  const [expanded, setExpanded] = useState(false);
  const outbound = item.direction === MessageDirection.OUTBOUND;
  const subject =
    typeof item.metadata?.subject === 'string' && item.metadata.subject.trim()
      ? item.metadata.subject
      : 'Sin asunto';
  const name = outbound
    ? 'Tú'
    : formatFullName(
        contact?.firstName,
        contact?.lastName,
        item.sender.displayName ?? 'Contacto'
      );
  const time = format(new Date(item.createdAt), 'HH:mm');
  return (
    <TimelineItemShell
      initial={isNew ? { opacity: 0, y: 8 } : false}
      className={classes(
        'w-fit max-w-[92%] sm:max-w-[80%] min-w-[180px] space-y-1 rounded-xl',
        outbound ? 'ml-auto bg-email-50' : 'mr-auto',
        getContextBorderTintClass(context),
        getContextOpacityClass(context)
      )}
    >
      <TimelineItemActions
        label={`Opciones del correo de ${name}, ${time}`}
        menuSide={outbound ? 'left' : 'right'}
        menuVariant={outbound ? 'email' : 'neutral'}
        copyText={`${subject}\n${item.content}`}
        details={
          <TimelineItemDetails
            channel={ChannelType.EMAIL}
            senderAddress={item.sender.address}
            recipientAddress={item.recipient?.address}
            description={`${name} · ${format(new Date(item.createdAt), "PP 'a las' HH:mm")}`}
          >
            {outbound && (
              <MessageStatusIndicator
                status={item.status}
                errorMessage={item.errors?.[0]?.message}
              />
            )}
          </TimelineItemDetails>
        }
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <div role="img" aria-label="Correo" className="shrink-0 flex">
            <ChannelIcon
              channelType={ChannelType.EMAIL}
              variant="email"
              size="sm"
            />
          </div>
          <Text size="small" weight="semibold" className="min-w-0 truncate">
            {name}
          </Text>
        </div>
        <Text
          size="small"
          weight="semibold"
          className="break-words [overflow-wrap:anywhere]"
        >
          {subject}
        </Text>
        <Text
          size="small"
          className={classes(
            'whitespace-pre-wrap break-words [overflow-wrap:anywhere]',
            !expanded && 'line-clamp-2'
          )}
        >
          {item.content}
        </Text>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            size="xs"
            appearance="ghost"
            variant="neutral"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Ver menos' : 'Ver correo'}
          </Button>
          {!!item.attachments?.length && (
            <Text size="tiny" className="flex items-center gap-1">
              <MdAttachment />
              {item.attachments.length} adjunto(s)
            </Text>
          )}
          <div className="flex items-center gap-1.5">
            <Text size="tiny" color="secondary">
              {format(new Date(item.createdAt), 'HH:mm')}
            </Text>
            {outbound && (
              <MessageStatusIndicator compact status={item.status} />
            )}
          </div>
        </div>
        <TimelineContextBadges context={context} />
      </TimelineItemActions>
    </TimelineItemShell>
  );
}
